/**
 * FormField — lightweight UI primitive wrapping form controls with
 * DaisyUI label + icon + error + hint chrome.
 *
 * Usage:
 *   <FormField label="Email" icon={Mail} error={errors.email?.message}>
 *     <input {...register("email")} className="input input-bordered w-full" />
 *   </FormField>
 */

import { cn } from "@lib/utils/cn";
import type { LucideIcon } from "lucide-react";
import { createElement, type ReactNode } from "react";

export interface FormFieldProps {
  /** Label text rendered above the child control */
  label?: string;
  /** Lucide icon rendered next to the label */
  icon?: LucideIcon;
  /** Marks the field as required — renders a `*` after the label in the danger color */
  required?: boolean;
  /** Error message — renders a DaisyUI label-text-alt with text-error */
  error?: string;
  /** Hint text — renders when no error is present */
  hint?: string;
  /** The form control (input, select, textarea, etc.) */
  children: ReactNode;
  /** Additional CSS classes forwarded to the form-control wrapper */
  className?: string;
}

export function FormField({
  label,
  icon,
  required,
  error,
  hint,
  children,
  className,
}: FormFieldProps) {
  return (
    <div className={cn("form-control", error && "has-error", className)}>
      {label && (
        <label className="label">
          <span className="label-text text-sm font-medium flex items-center gap-1.5">
            {icon && createElement(icon, { className: "w-4 h-4" })}
            {label}
            {required && <span className="text-error">*</span>}
          </span>
        </label>
      )}

      {children}

      {error && (
        <label className="label">
          <span className="label-text-alt text-error text-xs animate-fade-in">{error}</span>
        </label>
      )}

      {!error && hint && (
        <label className="label">
          <span className="label-text-alt text-base-content/50">{hint}</span>
        </label>
      )}
    </div>
  );
}

export default FormField;
